import * as React from 'react';
import { memo } from 'react';
import TerrificBridge from 'react-terrific-bridge';

import { LayoutTriggers, LayoutTriggerValueType } from 'defs/layout';
import { createRef } from 'react';

export type LayoutPropsType = {
	bodyLocked: boolean | null;
	goTopStamp: number | null;
	currentOptions?: Object;
	triggerComponent?: LayoutTriggerValueType;
};

export type LayoutStateType = {};

/*
 * Handling the global layout settings, fires
 * actions to the Terrific via the react-terrific-bridge.
 * @see PULSWM-2851
 * @author Jan Biasi <jan.biasi@namics.com>
 * @type {React.Component}
 */
export class Layout extends React.Component<LayoutPropsType, LayoutStateType> {
	public static displayName = 'Layout';

	public statefulTerrificAction: (
		component: React.ComponentType<any>,
		action: string,
		...args: any
	) => boolean | void = undefined as any;

	private componentRef: React.RefObject<HTMLDivElement>;

	constructor(props: LayoutPropsType) {
		super(props);
		this.componentRef = createRef();
	}

	public getTerrificBridgeOptions(nextProps?: LayoutPropsType): Object {
		const { currentOptions = {}, triggerComponent = LayoutTriggers.Unknown } = nextProps || this.props;

		return {
			...currentOptions,
			trigger: triggerComponent,
		};
	}

	public checkBooleanPropChanged(prevProps: LayoutPropsType, prop: string): boolean {
		return Boolean(this.props[prop] !== prevProps[prop] && prevProps[prop] !== null);
	}

	public checkTriggerComponentChanged(prevProps: LayoutPropsType): boolean {
		return prevProps.triggerComponent !== this.props.triggerComponent;
	}

	public componentDidMount() {
		TerrificBridge.registerComponent(this.componentRef.current);
		this.statefulTerrificAction = TerrificBridge.action.bind(TerrificBridge);
	}

	public componentWillUnmount() {
		TerrificBridge.unregisterComponent(this.componentRef.current);
		this.statefulTerrificAction = () => {
			/* prevent action on unmounted component */
		};
	}

	public componentDidUpdate(prevProps: LayoutPropsType) {
		const opts = this.getTerrificBridgeOptions(this.props);

		if (prevProps.goTopStamp === null && this.props.goTopStamp) {
			this.statefulTerrificAction(this.componentRef.current as any, 'goTop', opts);
		}

		if (this.checkTriggerComponentChanged(prevProps) || this.checkBooleanPropChanged(prevProps, 'bodyLocked')) {
			this.statefulTerrificAction(
				this.componentRef.current as any,
				this.props.bodyLocked ? 'lockBody' : 'unlockBody',
				opts
			);
		}
	}

	public render() {
		return <div id="o-layout" data-t-name="Layout" ref={this.componentRef} />;
	}
}

export default memo(Layout);
